import React from "react";
import styles from "../SubjectSinglePage.module.css";
import {
  CONTEXT_REPLY_BATCH_SIZE,
} from "../model/constants";
import type { GistItem, GistReaderMode, GistSiblingNavigation } from "../model/types";
import {
  countGistMetrics,
  getAncestorGists,
  getReplyGists,
  sortContextGists,
} from "../model/subjectSingleModel";

function isDraftGist(gist: GistItem): boolean {
  return gist.status?.toUpperCase() === "DRAFT";
}

type ReadingStripModeProps = {
  gist: GistItem;
  gists: GistItem[];
  selectedSubjectId: number | null;
  isRegisteredUser: boolean;
  readerMode: GistReaderMode;
  visibleReplyGistCount: number;
  expandedCommentPreviewGistIds: Set<number>;
  currentReadingGistRef: React.RefObject<HTMLElement>;
  currentGistSiblingNavigation: GistSiblingNavigation | null;
  renderReadArticle: (gist: GistItem, withMarkers?: boolean) => React.ReactNode;
  renderDiscussionPreview: (gist: GistItem) => React.ReactNode;
  onOpenGistReader: (gist: GistItem, mode?: GistReaderMode) => void;
  onOpenCommentWorkspace: (gist: GistItem) => void;
  onOpenGistAnnotationWorkspace: (gist: GistItem) => void;
  onOpenCreateGistModal: (target: {
    subjectId: number;
    parentGistId?: number;
    defaultSaveAsDraft?: boolean;
    intent?: "normal" | "linked-draft";
  }) => void;
  onChangeReaderMode: (mode: GistReaderMode) => void;
  onToggleCommentPreview: (gistId: number) => void;
  onLoadMoreReplies: () => void;
};

export const ReadingStripMode: React.FC<ReadingStripModeProps> = ({
  gist,
  gists,
  selectedSubjectId,
  isRegisteredUser,
  readerMode,
  visibleReplyGistCount,
  expandedCommentPreviewGistIds,
  currentReadingGistRef,
  currentGistSiblingNavigation,
  renderReadArticle,
  renderDiscussionPreview,
  onOpenGistReader,
  onOpenCommentWorkspace,
  onOpenGistAnnotationWorkspace,
  onOpenCreateGistModal,
  onChangeReaderMode,
  onToggleCommentPreview,
  onLoadMoreReplies,
}) => {
  const renderCurrentGistSiblingNavigation = () => {
    const previousSibling = currentGistSiblingNavigation?.previousSibling || null;
    const nextSibling = currentGistSiblingNavigation?.nextSibling || null;

    return (
      <div
        className={styles.siblingNavigation}
        aria-label="Sibling gist navigation"
      >
        <button
          type="button"
          disabled={!previousSibling}
          onClick={() => {
            if (previousSibling) onOpenGistReader(previousSibling, readerMode);
          }}
        >
          ← Previous gist
        </button>
        <button
          type="button"
          disabled={!nextSibling}
          onClick={() => {
            if (nextSibling) onOpenGistReader(nextSibling, readerMode);
          }}
        >
          Next gist →
        </button>
      </div>
    );
  };

  const renderReadingStripGist = (
    stripGist: GistItem,
    role: "ancestor" | "current" | "reply",
    index: number,
  ) => {
    const metrics = countGistMetrics(stripGist);
    const replies = getReplyGists(stripGist, gists).length;
    const isDraft = isDraftGist(stripGist);
    const roleLabel =
      role === "current"
        ? isDraft
          ? "Current draft"
          : "Current gist"
        : role === "ancestor"
          ? index === 0
            ? isDraft
              ? "Draft parent"
              : "Parent gist"
            : isDraft
              ? "Draft direct parent"
              : "Direct parent"
          : isDraft
            ? `Draft child ${index + 1}`
            : `Reply gist ${index + 1}`;
    const isCurrent = role === "current";
    const subjectId = selectedSubjectId || stripGist.subjectId;
    const canCreateReply = Boolean(subjectId && isRegisteredUser);

    return (
      <section
        key={`${role}-${stripGist.id}`}
        ref={isCurrent ? currentReadingGistRef : undefined}
        className={`${styles.readingStripGist} ${
          isCurrent
            ? styles.readingStripGistCurrent
            : styles.readingStripGistContext
        } ${isDraft ? styles.readingStripGistDraft : ""}`}
        aria-label={`${roleLabel}: ${stripGist.title}`}
      >
        <div className={styles.readingStripGistHeader}>
          <div>
            <span className={styles.readingStripGistRole}>{roleLabel}</span>
            {isDraft && (
              <span className={styles.readingStripDraftBadge}>Draft</span>
            )}
            {role === "reply" && (
              <span className={styles.readingStripRelationshipBadge}>
                Direct child of current gist
              </span>
            )}
          </div>
        </div>

        {renderReadArticle(stripGist, true)}

        <footer
          className={styles.readingStripActions}
          aria-label={`Actions for ${stripGist.title}`}
        >
          <button
            type="button"
            onClick={() => onOpenCommentWorkspace(stripGist)}
          >
            Comments Workspace ({metrics.commentCount})
          </button>
          <button
            type="button"
            onClick={() => onToggleCommentPreview(stripGist.id)}
            aria-expanded={expandedCommentPreviewGistIds.has(stripGist.id)}
          >
            {expandedCommentPreviewGistIds.has(stripGist.id) ? "Hide preview" : "Preview"}
          </button>
          <button
            type="button"
            disabled={!canCreateReply}
            onClick={() => {
              if (!subjectId) return;
              onOpenCreateGistModal({ subjectId, parentGistId: stripGist.id });
            }}
          >
            {isRegisteredUser ? "Reply with GIST" : "Login required"}
          </button>
          {!isCurrent && (
            <button
              type="button"
              onClick={() => onOpenGistReader(stripGist, "read")}
            >
              Make current
            </button>
          )}
          {isCurrent && (
            <button type="button" onClick={() => onChangeReaderMode("tools")}>
              Tools
            </button>
          )}
          {isCurrent && renderCurrentGistSiblingNavigation()}
          <button
            type="button"
            onClick={() => onOpenGistAnnotationWorkspace(stripGist)}
          >
            Annotate
          </button>
          <button
            type="button"
            onClick={() => onOpenGistReader(stripGist, "advanced")}
          >
            Advanced
          </button>
          <span>{replies} replies</span>
        </footer>
        {expandedCommentPreviewGistIds.has(stripGist.id) &&
          renderDiscussionPreview(stripGist)}
      </section>
    );
  };

  const ancestors = getAncestorGists(gist, gists).slice(-2);
  const replyGists = sortContextGists(getReplyGists(gist, gists));
  const visibleReplies = replyGists.slice(0, visibleReplyGistCount);
  const hiddenReplyCount = Math.max(0, replyGists.length - visibleReplies.length);

  return (
    <section className={styles.contextReadMode} aria-label="Gist reading strip">
      <div className={styles.readingStrip}>
        {ancestors.map((ancestor, index) =>
          renderReadingStripGist(ancestor, "ancestor", index),
        )}

        {renderReadingStripGist(gist, "current", 0)}

        {visibleReplies.map((reply, index) =>
          renderReadingStripGist(reply, "reply", index),
        )}

        {!visibleReplies.length && (
          <div className={styles.contextEmptyState}>
            <strong>No reply gists yet.</strong>
            <span>Create a new gist response to continue this branch.</span>
            <button
              type="button"
              disabled={!selectedSubjectId || !isRegisteredUser}
              onClick={() => {
                if (!selectedSubjectId) return;
                onOpenCreateGistModal({
                  subjectId: selectedSubjectId,
                  parentGistId: gist.id,
                });
              }}
            >
              {isRegisteredUser ? "Reply with new GIST" : "Login required"}
            </button>
          </div>
        )}

        {hiddenReplyCount > 0 && (
          <div className={styles.contextLoadMore}>
            <button type="button" onClick={onLoadMoreReplies}>
              Load next {Math.min(CONTEXT_REPLY_BATCH_SIZE, hiddenReplyCount)} reply gists ↓
            </button>
            <span>{hiddenReplyCount} still hidden</span>
          </div>
        )}
      </div>
    </section>
  );
};
